import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, User, Loader2, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { adminLogin, adminSetup, getAdminProfile } from '@/lib/api';
import { toast } from 'sonner';

import Logo from '@/components/Logo';

const LOGO_URL = "/logo.svg";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' or 'setup'
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });

  useEffect(() => {
    // Check if already logged in
    const checkAuth = async () => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        try {
          await getAdminProfile();
          navigate('/admin/dashboard');
        } catch (e) {
          localStorage.removeItem('admin_token');
        }
      }
      setCheckingAuth(false);
    };
    checkAuth();
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let response;
      if (mode === 'setup') {
        response = await adminSetup(formData);
        toast.success('Admin account created successfully!');
      } else {
        response = await adminLogin({ email: formData.email, password: formData.password });
        toast.success('Login successful!');
      }

      localStorage.setItem('admin_token', response.data.token);
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Auth error:', error);
      if (error.response?.data?.detail === 'Admin already exists. Use login.') {
        setMode('login');
        toast.error('Admin already exists. Please login.');
      } else {
        toast.error(error.response?.data?.detail || 'Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#7CC326] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4"><Logo className="h-14" /></div>
          <h1 className="text-2xl font-bold text-white">ChannelLoyalty Admin</h1>
          <p className="text-slate-400 mt-2">
            {mode === 'setup' ? 'Create your admin account' : 'Sign in to your account'}
          </p>
        </div>

        {/* Form */}
        <div className="glass rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'setup' && (
              <div>
                <Label htmlFor="name" className="text-slate-300">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="pl-10 bg-slate-800 border-slate-700 text-white"
                    placeholder="Your name"
                    data-testid="admin-name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="pl-10 bg-slate-800 border-slate-700 text-white"
                  placeholder="admin@channelloyalty.ai"
                  data-testid="admin-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="pl-10 pr-10 bg-slate-800 border-slate-700 text-white"
                  placeholder="••••••••"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  data-testid="toggle-password-visibility"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#2C39B8] hover:bg-[#2C39B8] text-white rounded-xl py-6 font-semibold"
              data-testid="admin-submit-btn"
            >
              {loading ? (
                <><Loader2 className="mr-2 animate-spin" /> {mode === 'setup' ? 'Creating...' : 'Signing in...'}</>
              ) : (
                mode === 'setup' ? 'Create Admin Account' : 'Sign In'
              )}
            </Button>
          </form>

          {/* Toggle mode */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setMode(mode === 'login' ? 'setup' : 'login')}
              className="text-[#7CC326] hover:text-green-300 text-sm"
            >
              {mode === 'login' ? 'First time? Create admin account' : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>

        {/* Back to site */}
        <div className="text-center mt-6">
          <a href="/" className="text-slate-500 hover:text-slate-400 text-sm">
            ← Back to ChannelLoyalty.ai
          </a>
        </div>
      </div>
    </div>
  );
}
